// Scaffolded clinical starter lists — the chip pools the consultation stages
// offer as quick-pick options.
//
// IMPORTANT: the option *labels* here are clinical CONTENT, not UI chrome, so
// they live as plain English data rather than i18n keys. They are reasonable
// scaffolded defaults pulled from the design; a later stage may make them
// clinic-configurable. The structural labels around them (section titles,
// "Other", etc.) DO go through i18n in the stage components.
//
// No React, no I/O — pure data. The single source of truth for the option pools.

import type { SystemsReview } from '@/types/patients';

export interface SystemReviewDef {
  /** Must be a key of the `SystemsReview` contract object. */
  key: keyof SystemsReview;
  /** i18n key for the human-readable system name (the chrome label). */
  labelKey: string;
  /** Quick-pick finding chips for this system (clinical content, English). */
  findings: string[];
}

// The 8 contract systems, each mapped to its `SystemsReview` key. Finding chips
// are adapted from the design's SYSTEMS pool and folded onto these 8 keys
// (the design's "General" findings are merged into the closest systems).
export const SYSTEM_REVIEW: SystemReviewDef[] = [
  {
    key: 'cardiovascular',
    labelKey: 'consult.systems.cardiovascular',
    findings: ['Chest pain', 'Palpitations', 'Exertional dyspnoea', 'Orthopnoea', 'PND', 'Ankle swelling', 'Syncope'],
  },
  {
    key: 'respiratory',
    labelKey: 'consult.systems.respiratory',
    findings: ['Cough', 'Sputum', 'Haemoptysis', 'Wheeze', 'Breathlessness', 'Pleuritic pain'],
  },
  {
    key: 'gastrointestinal',
    labelKey: 'consult.systems.gastrointestinal',
    findings: [
      'Nausea',
      'Vomiting',
      'Abdominal pain',
      'Diarrhoea',
      'Constipation',
      'Dysphagia',
      'Haematemesis',
      'Melaena',
      'Jaundice',
      'Loss of appetite',
    ],
  },
  {
    key: 'genitourinary',
    labelKey: 'consult.systems.genitourinary',
    findings: ['Dysuria', 'Frequency', 'Urgency', 'Haematuria', 'Incontinence', 'Discharge'],
  },
  {
    key: 'neurological',
    labelKey: 'consult.systems.neurological',
    findings: ['Headache', 'Dizziness', 'Seizures', 'Weakness', 'Numbness', 'Visual disturbance', 'Loss of consciousness'],
  },
  {
    key: 'musculoskeletal',
    labelKey: 'consult.systems.musculoskeletal',
    findings: ['Joint pain', 'Joint swelling', 'Stiffness', 'Back pain', 'Muscle weakness'],
  },
  {
    key: 'dermatological',
    labelKey: 'consult.systems.dermatological',
    findings: ['Rash', 'Itch', 'Skin lesion', 'Bruising', 'Hair loss', 'Nail changes'],
  },
  {
    key: 'endocrine',
    labelKey: 'consult.systems.endocrine',
    findings: ['Polyuria', 'Polydipsia', 'Heat intolerance', 'Cold intolerance', 'Tremor', 'Weight loss', 'Weight gain'],
  },
];

// Common previous-illness quick-picks (autosuggest pool, clinical content).
export const PREVIOUS_ILLNESSES: string[] = [
  'Dengue',
  'Typhoid',
  'Tuberculosis',
  'Hepatitis',
  'Pneumonia',
  'Rheumatic fever',
  'Malaria',
  'Chickenpox',
];

// Common major-surgery quick-picks (autosuggest pool, clinical content).
export const COMMON_SURGERIES: string[] = [
  'Appendicectomy',
  'Cholecystectomy',
  'LSCS',
  'Hernia repair',
  'Tonsillectomy',
  'Hysterectomy',
  'CABG',
  'Cataract surgery',
];

// Chronic / non-communicable disease quick-picks.
export const CHRONIC_DISEASES: string[] = [
  'DM',
  'HTN',
  'DL',
  'IHD',
  'CKD',
  'Cirrhosis',
  'Asthma',
  'COPD',
  'Hypothyroidism',
  'Epilepsy',
];

// Complications recorded against the current / a past pregnancy.
export const PREGNANCY_COMPLICATIONS: string[] = [
  'GDM',
  'Pre-eclampsia',
  'Anaemia',
  'APH',
  'Hyperemesis',
  'UTI',
  'Gestational HTN',
  'IUGR',
];

// Common working-diagnosis quick-picks (autosuggest pool, clinical content).
// Custom diagnoses are persisted clinic-wide and merged in via `useSuggestions`.
export const DIAGNOSIS_PRESETS: string[] = [
  'Viral fever',
  'Dengue fever',
  'Upper respiratory tract infection',
  'Lower respiratory tract infection',
  'Urinary tract infection',
  'Acute gastroenteritis',
  'Hypertension',
  'Type 2 diabetes mellitus',
  'Bronchial asthma',
  'Acute pharyngitis',
  'Migraine',
  'Anaemia',
];

// Standard referral targets (preset chips). The clinician can add custom
// targets, which are persisted clinic-wide and merged in via `useSuggestions`.
export const REFERRAL_PRESETS: string[] = [
  'Dermatologist',
  'Chest Clinic',
  'VOG',
  'VOG Clinic',
];

// Common prescribed-investigation quick-picks (autosuggest pool). Custom
// investigations are persisted clinic-wide and merged in via `useSuggestions`.
export const INVESTIGATION_PRESETS: string[] = [
  'FBC',
  'ESR',
  'CRP',
  'FBS',
  'RBS',
  'HbA1c',
  'SE',
  'S. creatinine',
  'LFT',
  'Lipid profile',
  'TSH',
  'UFR',
  'ECG',
  'CXR',
  'USS abdomen',
];

// Conditions a family history can flag.
export const HEREDITARY_CONDITIONS: string[] = [
  'Diabetes',
  'Hypertension',
  'Ischaemic heart disease',
  'Stroke',
  'Cancer',
  'Asthma',
  'Thyroid disease',
  'Mental illness',
];

// Living-condition quick-picks (folded into `social.occupationNotes`).
export const LIVING_CONDITIONS: string[] = ['Lives alone', 'With family', 'Institution'];

// Patient smoking status (single-choice). Stored verbatim in `social.smoking`.
export const SMOKING_OPTIONS: string[] = ['Never', 'Ex-smoker', 'Current'];

// Patient alcohol use (single-choice). Stored verbatim in `social.alcohol`.
export const ALCOHOL_OPTIONS: string[] = ['Never', 'Occasional', 'Regular'];

// Contraception options for the gynae snapshot.
export const CONTRACEPTION_OPTIONS: string[] = [
  'None',
  'None — planning',
  'OCP',
  'POP',
  'IUCD',
  'Implant',
  'Injectable',
  'Condom',
  'Sterilised',
];

// Mode of delivery for a pregnancy record.
export const PREGNANCY_MODES: string[] = ['NVD', 'LSCS', 'Assisted', 'Instrumental'];

// Outcome of a pregnancy record.
export const PREGNANCY_OUTCOMES: string[] = [
  'Live birth',
  'Stillbirth',
  'Miscarriage',
  'Ectopic',
  'Termination',
  'Ongoing',
];
